import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Pending as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Refresh as RefreshIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ClubService from '../services/clubService';
import { ClubApplication } from '../types';

interface MyApplicationWithClub {
  applicationId: string;
  club: {
    id: string;
    name: string;
    description: string;
    logo: string;
    facultyMentor: { name: string; email: string };
  };
  status: 'pending' | 'approved' | 'rejected';
  score: number;
  percentage: number;
  motivation: string;
  appliedAt: string;
  reviewedAt?: string;
  feedback?: string;
}

const MyApplicationsPage: React.FC = () => {
  const [applications, setApplications] = useState<MyApplicationWithClub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reconsiderDialog, setReconsiderDialog] = useState<{
    open: boolean;
    applicationId: string;
    clubName: string;
  }>({ open: false, applicationId: '', clubName: '' });
  const [reconsiderReason, setReconsiderReason] = useState('');
  const [submittingReconsider, setSubmittingReconsider] = useState(false);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ClubService.getUserApplications();
      setApplications(response.applications);
    } catch (error: any) {
      console.error('Failed to load applications:', error);
      setError('Failed to load applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReconsideration = async () => {
    if (!reconsiderReason.trim()) return;

    try {
      setSubmittingReconsider(true);

      // Find the application to get club ID
      const application = applications.find(app => app.applicationId === reconsiderDialog.applicationId);
      if (!application) {
        setError('Application not found');
        return;
      }

      await ClubService.requestReconsideration(
        application.club.id,
        reconsiderDialog.applicationId,
        reconsiderReason
      );
      setReconsiderDialog({ open: false, applicationId: '', clubName: '' });
      setReconsiderReason('');
      loadApplications(); // Refresh the list
    } catch (error: any) {
      console.error('Failed to request reconsideration:', error);
      setError('Failed to request reconsideration. Please try again.');
    } finally {
      setSubmittingReconsider(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Chip
            icon={<PendingIcon />}
            label="Pending Review"
            color="warning"
            variant="outlined"
          />
        );
      case 'approved':
        return (
          <Chip
            icon={<ApprovedIcon />}
            label="Approved"
            color="success"
          />
        );
      case 'rejected':
        return (
          <Chip
            icon={<RejectedIcon />}
            label="Rejected"
            color="error"
          />
        );
      default:
        return (
          <Chip
            label={status}
            color="default"
          />
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading your applications..." />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            My Applications
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Track your club membership applications
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadApplications}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Applications List */}
      {applications.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <QuizIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              No Applications Yet
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              You haven't applied to any clubs yet. Browse clubs and take their entry quizzes to join!
            </Typography>
            <Button variant="contained" href="/clubs">
              Browse Clubs
            </Button>
          </CardContent>
        </Card>
      ) : (
        <List sx={{ bgcolor: 'background.paper' }}>
          {applications.map((application) => (
            <Card key={application.applicationId} sx={{ mb: 2 }}>
              <ListItem sx={{ p: 3 }}>
                <ListItemAvatar>
                  <Avatar sx={{ width: 56, height: 56 }}>
                    {application.club.logo || application.club.name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  sx={{ ml: 2 }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {application.club.name}
                      </Typography>
                      {getStatusChip(application.status)}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Quiz Score: {application.percentage}% ({application.score} correct)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Applied: {formatDate(application.appliedAt)}
                      </Typography>
                      {application.reviewedAt && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Reviewed: {formatDate(application.reviewedAt)}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Faculty Mentor: {application.club.facultyMentor.name}
                      </Typography>
                      {application.feedback && (
                        <Alert severity={application.status === 'approved' ? 'success' : 'info'} sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            <strong>Feedback:</strong> {application.feedback}
                          </Typography>
                        </Alert>
                      )}
                      {application.motivation && (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Your Motivation:</strong> {application.motivation}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />

                {application.status === 'rejected' && (
                  <Box sx={{ ml: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        setReconsiderDialog({
                          open: true,
                          applicationId: application.applicationId,
                          clubName: application.club.name,
                        })
                      }
                    >
                      Request Reconsideration
                    </Button>
                  </Box>
                )}
              </ListItem>
            </Card>
          ))}
        </List>
      )}

      {/* Request Reconsideration Dialog */}
      <Dialog
        open={reconsiderDialog.open}
        onClose={() => setReconsiderDialog({ open: false, applicationId: '', clubName: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Request Reconsideration - {reconsiderDialog.clubName}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for requesting reconsideration of your rejected application.
            This will be reviewed by the club administrators.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for Reconsideration"
            placeholder="Explain why you believe your application should be reconsidered..."
            value={reconsiderReason}
            onChange={(e) => setReconsiderReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReconsiderDialog({ open: false, applicationId: '', clubName: '' });
              setReconsiderReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRequestReconsideration}
            disabled={!reconsiderReason.trim() || submittingReconsider}
          >
            {submittingReconsider ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyApplicationsPage;